import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "ceco-polyglot";
import { Header } from "../../components/cecoadmin-panel";
import RecipesTable from "../../components/tables/RecipesTable";
import RecipesCards from "../../components/RecipesCards";

import FullWidthLayout from "../../layout/FullWidthLayout";
import CreateButton from "../../components/CreateButton";
import TogglerHeader from "../../components/TogglerHeader";
import FiltersRecipes from "./FiltersRecipes";
import { Dimmer, Loader, Button } from "semantic-ui-react";

import graphql from "babel-plugin-relay/macro";
import { createPaginationContainer } from "react-relay";

import "./recipesPage.scss";

const RecipesPage = (props) => {
  const {
    sidebarExpanded,
    setSidebarExpanded,
    setSidebarProps,
    menuExpanded,
    setMenuExpanded,
    children,
    match,
    allCategories,
    allRecipes,
    allUsers,
    allLabel,
    relay,
    ...rest
  } = props;

  const { recipeId } = match.params;
  const [sidebarActiveCount, setSidebarActiveCount] = useState(0);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState();
  const [orderBy, setOrderBy] = useState("published");
  const [cardMode, setCardMode] = useState(false);
  const [isFixed] = useState(false);

  const t = useTranslation();

  const { pageInfo, totalCount, edges } = allRecipes.allRecipes;
  const [isLoading, setIsLoading] = useState(false);
  const loadSearch = useRef(false);

  useEffect(() => {
    document.title = `Recetario Mambo - ${t("title.recipes")}`;
  }, [t]);

  const _loadMore = () => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    setIsLoading(true);

    relay.loadMore(20, () => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (loadSearch.current) {
      setIsLoading(true);
      relay.refetchConnection(
        search === "" || search === undefined ? 20 : null,
        () => setIsLoading(false),
        {
          name: search === "" || search === undefined ? null : search,
          isTrend: filters?.isTrend,
          author: filters?.author,
          orderBy: orderBy,
          category: filters?.category,
          publicDateFrom: filters?.dateFilter?.startDate,
          publicDateTo: filters?.dateFilter?.endDate,
        }
      );
    }
    loadSearch.current = true;
  }, [filters, loadSearch, relay, search, orderBy]);

  useEffect(() => {
    setSidebarProps({
      title: "Filtros",
      isFixed,
      children: (
        <FiltersRecipes
          setSidebarActiveCount={setSidebarActiveCount}
          filters={filters}
          setFilters={setFilters}
          allCategories={allCategories}
          allUsers={allUsers}
        />
      ),
    });
  }, [
    filters,
    isFixed,
    setSidebarProps,
    setSidebarActiveCount,
    allCategories,
    allUsers,
  ]);

  const refetchData = () => {
    setIsLoading(true);

    relay.refetchConnection(
      search === "" || search === undefined ? 20 : null,
      () => setIsLoading(false),
      {
        name: search === "" || search === undefined ? null : search,
        isTrend: filters?.isTrend,
        author: filters?.author,
        orderBy: orderBy,
        category: filters?.category,
        publicDateFrom: filters?.dateFilter?.startDate,
        publicDateTo: filters?.dateFilter?.endDate,
      }
    );
  };

  const recipesParsed = edges.map((recipe, i) => {
    return {
      ...recipe.node,
      category: recipe.node.category,
      categoryName: recipe.node.category.name,
    };
  });

  const allLabelParsed = allLabel.edges.map((label, i) => {
    return {
      ...label.node,
    };
  });

  const handleScroll = (e) => {
    const target = e.target;

    if (target.scrollHeight - target.scrollTop === target.clientHeight) {
      _loadMore();
    }
  };

  const onSearchText = useCallback(
    (txt) => {
      setSearch(txt);
    },
    [setSearch]
  );

  return (
    <FullWidthLayout>
      <Header
        title={
          recipeId !== undefined
            ? t("title.recipe") + " " + recipeId
            : t("title.recipes")
        }
        menuExpanded={menuExpanded}
        setMenuExpanded={setMenuExpanded}
        sidebarExpanded={sidebarExpanded}
        setSidebarExpanded={setSidebarExpanded}
        sidebarActiveCount={sidebarActiveCount}
        onSearchTextChange={onSearchText}
      >
        <CreateButton
          allLabel={allLabelParsed}
          allCategories={allCategories}
          refetchData={refetchData}
        />
      </Header>

      {!recipeId && (
        <TogglerHeader
          setOrderBy={setOrderBy}
          filters={filters}
          cardMode={cardMode}
          setCardMode={setCardMode}
        />
      )}

      <>
        {recipesParsed &&
          (cardMode ? (
            <div className="recipes card-wrapper">
              <RecipesCards
                recipesParsed={recipesParsed} //   totalCount,
                refetchData={refetchData}
                pageInfo={pageInfo}
                totalCount={totalCount}
                filters={filters}
                setFilters={setFilters}
                allCategories={allCategories}
                allLabel={allLabelParsed}
                relay={relay}
                {...rest}
              />

              {!search && <Button onClick={_loadMore}>Cargar mas</Button>}

              {isLoading && (
                <Dimmer inverted active>
                  <Loader indeterminate />
                </Dimmer>
              )}
            </div>
          ) : (
            <div className="recipes table-wrapper">
              <RecipesTable
                recipesParsed={recipesParsed} //   totalCount,
                refetchData={refetchData}
                pageInfo={pageInfo}
                totalCount={totalCount}
                filters={filters}
                setFilters={setFilters}
                allCategories={allCategories}
                allLabel={allLabelParsed}
                relay={relay}
                handleScroll={handleScroll}
                {...rest}
              />

              {isLoading && (
                <Dimmer inverted active>
                  <Loader indeterminate />
                </Dimmer>
              )}
            </div>
          ))}
      </>
    </FullWidthLayout>
  );
};

export default createPaginationContainer(
  RecipesPage,
  {
    allRecipes: graphql`
      fragment RecipesPage_allRecipes on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String" }
        name: { type: "String" }
        category: { type: "ID" }
        orderBy: { type: "[String]", defaultValue: "published" }
        after: { type: "String" }
        author: { type: "ID" }
        publicDateFrom: { type: "String" }
        publicDateTo: { type: "String" }
        isTrend: { type: "Boolean" }
      ) {
        allRecipes(
          name: $name
          first: $count
          after: $cursor
          orderBy: $orderBy
          category: $category
          after: $after
          author: $author
          publicDateFrom: $publicDateFrom
          publicDateTo: $publicDateTo
          isTrend: $isTrend
        ) @connection(key: "Recipes_allRecipes") {
          totalCount
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              isTrend
              name
              category {
                id
                name
              }
              serves
              favoritedBy {
                edges {
                  node {
                    username
                  }
                }
              }
              medRatings
              totalRatings
              published
              hotKeep
              picture
              difficulty
              description
              labels {
                edges {
                  node {
                    id
                    name
                  }
                }
              }
              mediaFiles {
                edges {
                  node {
                    id
                    mediaUrl
                  }
                }
              }
            }
          }
        }
      }
    `,
  },
  {
    direction: "forward",
    getConnectionFromProps(props) {
      return props.allRecipes && props.allRecipes.allRecipes;
    },
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return {
        count,
        cursor,
        orderBy: fragmentVariables.orderBy,
      };
    },
    query: graphql`
      query RecipesPagePaginationQuery(
        $count: Int
        $cursor: String
        $name: String
        $category: ID
        $orderBy: [String]
        $author: ID
        $publicDateFrom: String
        $publicDateTo: String
        $isTrend: Boolean
      ) {
        ...RecipesPage_allRecipes
          @arguments(
            count: $count
            cursor: $cursor
            name: $name
            category: $category
            orderBy: $orderBy
            author: $author
            publicDateFrom: $publicDateFrom
            publicDateTo: $publicDateTo
            isTrend: $isTrend
          )
      }
    `,
  }
);
